import { Link } from "react-router-dom";
import { ShieldCheck, Video, CalendarCheck, MessageCircle, BadgeCheck, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1541089404510-5c9a779841fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNvdXBsZSUyMDQwcyUyMDUwcyUyMGxpZmVzdHlsZXxlbnwwfHx8fDE3ODk2MzUxMjZ8MA&ixlib=rb-4.1.0&q=85";

const SHOWCASE = [
  {
    name: "Elena, 52",
    location: "San Francisco, CA",
    url: "https://images.pexels.com/photos/20819288/pexels-photo-20819288.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "Appreciates Sunday farmers markets, jazz records, and honest conversations.",
  },
  {
    name: "Marcus, 56",
    location: "Denver, CO",
    url: "https://images.pexels.com/photos/26733193/pexels-photo-26733193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "Life is richer when shared. Passionate about mountain trails and craft cider.",
  },
  {
    name: "Diane, 49",
    location: "Austin, TX",
    url: "https://images.unsplash.com/photo-1758686254563-5c5ab338c8b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtYXR1cmUlMjBzbWlsaW5nJTIwYWR1bHQlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODk2MzUxMjZ8MA&ixlib=rb-4.1.0&q=85",
    bio: "Believer in kind hearts, spontaneous road trips, and dinner from scratch.",
  },
];

const STATS = [
  ["100%", "Human Screened"],
  ["0", "Romance Scams"],
  ["48", "Average Age"],
  ["5 min", "Live Video Check"],
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="texture-grain">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-5">Dating for grown-ups</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 leading-[1.08]">
            Zero bots. <span className="italic text-[#1F3A34]">100% video-verified</span> mature romance.
          </h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">
            Every member of Veritas40+ is personally met on a live video call by our Trust &amp; Safety
            officers before they can browse or message anyone. No catfish. No scammers. Just real people
            over 40, looking for something genuine.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            {user ? (
              <Link to="/discover" className="btn-primary text-base" data-testid="landing-hero-cta-button">
                Browse verified members
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn-primary text-base" data-testid="landing-hero-cta-button">
                  Start your verified journey
                </Link>
                <Link to="/register" className="btn-accent text-base" data-testid="landing-book-verification-button">
                  <Video size={18} /> Book your interview
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="card-surface overflow-hidden">
            <img src={HERO_IMG} alt="Happy mature couple embracing outdoors" className="w-full h-80 object-cover" />
            <div className="p-5 flex items-start gap-3">
              <span className="mt-0.5 w-9 h-9 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] flex items-center justify-center text-[#065F46]">
                <BadgeCheck size={18} />
              </span>
              <div>
                <p className="font-semibold text-stone-800 text-sm">Every profile you see passed a live video interview</p>
                <p className="text-xs text-stone-500 mt-1 font-dm uppercase tracking-wider">
                  Identity confirmed · Photo matched · Pledge signed
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-5 -left-5 card-surface px-4 py-3 hidden sm:flex items-center gap-2">
            <Lock size={14} className="text-[#C58342]" />
            <span className="text-xs font-medium text-stone-600">Unverified members cannot message anyone</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-[#E7DFD5] bg-[#F3EFEA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map(([num, label]) => (
            <div key={label} className="text-center">
              <p className="font-display text-4xl text-[#1F3A34]">{num}</p>
              <p className="mt-1 text-xs font-dm uppercase tracking-widest text-stone-500">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <p className="eyebrow mb-3">How verification works</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-stone-900 mb-12">
          Three steps to genuine connection
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: <ShieldCheck size={22} />,
              step: "Step 1",
              title: "Curate your mature profile",
              text: "Share who you really are — your story, interests, and what you're looking for at this stage of life.",
            },
            {
              icon: <CalendarCheck size={22} />,
              step: "Step 2",
              title: "5-minute live video interview",
              text: "Book a slot with a Veritas verification officer. A friendly face-to-face call confirms you are who you say you are.",
            },
            {
              icon: <MessageCircle size={22} />,
              step: "Step 3",
              title: "Unlock matchmaking & messaging",
              text: "Once approved, browse verified members, like, match, and message — with total peace of mind.",
            },
          ].map((s) => (
            <div key={s.step} className="card-surface p-7 hover:shadow-md transition-shadow duration-300">
              <span className="w-11 h-11 rounded-xl bg-[#1F3A34] text-[#FAF8F5] flex items-center justify-center">
                {s.icon}
              </span>
              <p className="mt-5 text-xs font-dm uppercase tracking-widest text-[#C58342]">{s.step}</p>
              <h3 className="mt-2 font-display text-xl text-stone-800">{s.title}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Recently verified</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-stone-900">Members who passed the interview</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SHOWCASE.map((m) => (
            <div key={m.name} className="card-surface overflow-hidden group" data-testid="showcase-member-card">
              <div className="relative h-72 overflow-hidden">
                <img
                  src={m.url}
                  alt={m.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 badge-verified shadow-sm">
                  <BadgeCheck size={12} /> Video Verified
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg text-stone-900">{m.name}</h3>
                  <span className="text-xs text-stone-500">{m.location}</span>
                </div>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{m.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Safety pledge */}
      <section className="bg-[#1F3A34] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-dm uppercase tracking-widest text-[#C58342] mb-4">Our safety pledge</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight">
              We meet every single member on camera. So you never have to wonder.
            </h2>
          </div>
          <ul className="space-y-4 text-sm text-[#FAF8F5]/85">
            {[
              "Live face-to-face video interview with a trained Trust & Safety officer",
              "Government photo ID check and live face match during the call",
              "Members sign the Safe Member Pledge before joining the community",
              "Instant suspension and investigation of any reported fraud",
              "Payments protected by Stripe with SSL encryption",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <BadgeCheck size={16} className="mt-0.5 shrink-0 text-[#C58342]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E7DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-display text-lg text-stone-800">
            Veritas<span className="text-[#C58342]">40+</span>
          </p>
          <p className="text-xs text-stone-500 font-dm uppercase tracking-widest">
            Dating for grown-ups · 100% video-verified · Est. 2026
          </p>
        </div>
      </footer>
    </div>
  );
}
